import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { useInView } from 'react-intersection-observer';
import { Waypoint } from 'react-waypoint';

import Content from '../../widgets/core/content';

import { AssetContentViewer } from './../../AssetViewer/viewer';
import { WidgetsContext, GatingContext } from "../core";
import { AutoGrowLayout } from "../autoGrowLayout";
import { BackgroundView } from '../background';

import styles from './asset.module.scss';

const AssetWidgetComponent = ({ className, style, widgetProps, breakpoint }) => {
  const assetRef = React.useRef(null);
  const context = React.useContext(WidgetsContext);
  const contentContext = React.useContext(Content.Context);
  const gatingContext = React.useContext(GatingContext);

  const asset = _.get(widgetProps, 'content')

  const [active, setActive] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(null);
  const [loaded, setLoaded] = useState(false);
  const [gatingConfigurations, setGatingConfigurations] = React.useState(_.get(widgetProps, 'content.content.gatingConfigurations', []));

  const handleAssetVisibilityChange = (visible) => {
    setActive(visible);
  }

  const id = _.get(widgetProps, 'uid');
  const fullHeight = _.get(widgetProps, 'properties.desktop.fullHeight', false);
  const content = _.get(widgetProps, 'content', null);
  const background = _.get(widgetProps, 'properties.desktop.background');
  const poster = _.get(widgetProps, 'properties.desktop.video.poster');

  const activeSession = useSelector(state => _.get(state, `content.analytics.${_.get(widgetProps, 'content.id', null)}.item.id`));

  var scrollParent = fullHeight ? 'body' : `#asset_scroll_${id}`;

  var widgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint, loaded)
  }

  useEffect(() => {
    const updatedItems = (gatingConfigurations || []).filter(({ id }) => !gatingContext.assetConfigurations.includes(id));
    setGatingConfigurations(updatedItems);
  }, [gatingContext.assetConfigurations])

  useEffect(() => {
    if (gatingContext.configuration) {
      return;
    }
    if (analytics) {
      setLoaded(true);
      triggerAssetGating(analytics)
    }
  }, [analytics])

  const triggerAssetGating = ({ duration, percentageRead }) => {
    if (gatingContext.preview) {
      return;
    }

    gatingConfigurations.every(item => {
      if (!item.enabled) {
        return true;
      }

      var triggerValue = _.get(item, 'trigger.value', 0);
      var limit = duration;

      if (_.get(item, 'trigger.type', '') === "PERCENTAGE") {
        limit = percentageRead;
      }

      if (triggerValue <= limit) {
        var gating = _.get(item, 'gating.id', null);

        if (gating) {
          context.fetchGating(gating);
          gatingContext.triggerGating(_.get(item, 'trigger.type', ''), null, item);
          return false;
        }
      }

      return true;
    })
  }

  if (!content || context.development) {
    return null;
  }

  return (
    <AutoGrowLayout ref={assetRef} editorSession={context.session} breakpoint={breakpoint} widget={widgetProps} inlineAsset>
      <Waypoint onEnter={() => handleAssetVisibilityChange(true)} onLeave={() => handleAssetVisibilityChange(false)}>
        <div id={id}
          ref={assetRef}
          className={cx(className, styles.asset, 'cs_asset_widget')}
          style={widgetStyle}
          data-widget-id={id}
          data-asset={asset.uid}
        >
          <div id={`asset_scroll_${id}`} className={styles.assetViewer} style={getScrollStyles(widgetProps)}>
            <BackgroundView background={background} breakpoint={breakpoint} className={cx({ [styles.background]: !fullHeight })} />
            <AssetContentViewer
              asset={widgetProps.content}
              active={active}
              poster={poster}
              inline={true}
              widgetId={id}
              onLoad={() => {
                setLoaded(true)
              }}
              loaded={loaded}
              scrollParent={scrollParent}
              onAnalyticsChange={(analytics) => {
                if (activeSession || active) {
                  setAnalytics(analytics);
                  contentContext.onAnalyticsChange(asset, analytics);
                }
              }}
              onDebugAnalytics={(analytics) => {
                if (contentContext.debug) {
                  setAnalytics(analytics);
                }
              }}
            />
          </div>
        </div>
      </Waypoint>
    </AutoGrowLayout>
  );
}

function getScrollStyles(widgetProps) {
  const fullHeight = _.get(widgetProps, 'properties.desktop.fullHeight', false);

  var widgetStyles = {};

  if (fullHeight) {
    widgetStyles = {
      ...widgetStyles,
      overflowY: 'hidden'
    }
  } else {
    widgetStyles = {
      ...widgetStyles,
      overflowY: 'auto'
    }
  }

  return widgetStyles;
}

function getStyles(widgetProps, breakpoint, loaded) {
  const display = _.get(widgetProps, 'properties.desktop.display', 'PAGE');
  const fullHeight = _.get(widgetProps, 'properties.desktop.fullHeight', false);
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const height = _.get(widgetProps, `properties.${breakpoint}.layout.height`, 'auto');
  const width = _.get(widgetProps, `properties.${breakpoint}.layout.width`, '100%');

  var widgetStyles = {}

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      width: `1200px`,
    }

    if (display === 'STRETCHED') {
      widgetStyles = {
        ...widgetStyles,
        marginLeft: margin,
        width: `calc(100% - 2 * ${margin}px)`,
      }
    }

    if(display === 'FREE') {
      widgetStyles = {
        ...widgetStyles,
        width: width,
      }
    }
  }

  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
      width: '100%',
    }
  }

  if (fullHeight) {
    widgetStyles = {
      ...widgetStyles,
      // overflowY: 'hidden'
    }

    if (loaded) {
      widgetStyles = {
        ...widgetStyles,
        height: 'auto',
      }
    } else {
      widgetStyles = {
        ...widgetStyles,
        height: height,
      }
    }
  } else {
    widgetStyles = {
      ...widgetStyles,
      // overflowY: 'auto'
    }
  }

  return widgetStyles;
}

const assetWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const AssetWidget = React.memo(AssetWidgetComponent, assetWidgetEquals);
